import { pageSearchField, typeInto } from "../components/EstateAskMic";

/**
 * What the page itself can be asked and told.
 * ---------------------------------------------------------------------------
 * `navigate_to_page` can put somebody in front of the right thing. These are
 * the tools for once they are there: looking at what is on screen, searching
 * it, changing how it is drawn, opening one of the things on it.
 *
 * Which matters more than it sounds. A model with only `navigate_to_page` is
 * working blind - it cannot tell whether the page it asked for shows anything,
 * cannot answer "how many", and cannot do the second half of a request like
 * "find the postgres box and open it". These give it eyes and hands for the
 * page it is standing on, and they are the same doors a person uses: the
 * search field, the view toggle, a click on a card.
 *
 * Everything here reads or drives the real page. Nothing is a model of it -
 * so nothing can be out of date, and a tool that finds no control says so
 * rather than claiming to have pressed one.
 * ---------------------------------------------------------------------------
 */

const text = (el) => (el?.textContent || "").replace(/\s+/g, " ").trim();

/* How long to let a page redraw after being told to do something, before
   reporting what it looks like now. */
const SETTLE = 700;
const wait = (ms = SETTLE) => new Promise(r => setTimeout(r, ms));

/** Every card on a card page, as the thing it is showing. */
function cardsNow() {
  return [...document.querySelectorAll(".forge-wiki-card")].map(c => ({
    id: c.getAttribute("data-card-id") || null,
    name: text(c.querySelector(".forge-wiki-card__name")),
    where: text(c.querySelector(".forge-wiki-card__repo")),
    selected: c.classList.contains("is-selected"),
    open: c.classList.contains("is-focused"),
    el: c,
  }));
}

/**
 * The rows of whatever table is up, as the first few things each one says.
 *
 * By what is in the cells rather than by column: a table here carries hidden
 * columns, an expander and a checkbox, and pairing those with their headers
 * produced rows that read "Name : v.0ip-10-0-2-50 · Tags: +" - true, and no
 * use at all to anything trying to understand what is on the page.
 */
function rowsNow(limit = 12) {
  return [...document.querySelectorAll("table tbody tr")]
    .map(r => [...r.children]
      .map(c => text(c))
      .filter(t => t.length > 1 && !/^[+\u2022\u22ee]+$/.test(t))
      .slice(0, 4)
      .map(t => t.slice(0, 44))
      .join(" · "))
    .filter(Boolean)
    .slice(0, limit);
}

/** What the page is, right now, whoever is looking. */
/* ----------------------------------------------------- what is on offer */

/**
 * Every control this page is actually showing, found by looking.
 *
 * The alternative was a hand-written list of what each page can do, which
 * would be wrong the day after it was written: the pages gain buttons weekly,
 * and a tool that claims an operation the page no longer has is worse than one
 * that claims none. So nothing is declared. The page is read - the same way
 * somebody looking at it would - and what comes back is whatever is on screen
 * at the moment of asking.
 *
 * What counts as a control: anything that answers to a click and says what it
 * is. What does not: the things that are the same on every page (the rail, the
 * ask) and anything invisible, disabled or nameless - a button nobody can see
 * is not an operation that is available.
 */
const CHROME = ".left-side, .side-menu, .estate-voice, .estate-ask";

function visible(el) {
  if (el.disabled || el.getAttribute("aria-hidden") === "true") return false;
  const box = el.getBoundingClientRect();
  if (box.width < 4 || box.height < 4) return false;
  const seen = getComputedStyle(el);
  return seen.visibility !== "hidden" && seen.display !== "none" && Number(seen.opacity) > 0.05;
}

/** What to call it: its own words first, then whatever it was labelled with. */
function nameOf(el) {
  const own = text(el);
  const said = el.getAttribute("aria-label") || el.getAttribute("title") || "";
  /* A button with an icon in it has no text, which is most of the toolbar; one
     with both is named by its text, since that is what is read off the screen. */
  const name = (own || said).trim();
  return name.length > 60 ? `${name.slice(0, 57)}…` : name;
}

/** Whether it is on, for the ones that hold a state. */
function stateOf(el) {
  const pressed = el.getAttribute("aria-pressed") ?? el.getAttribute("aria-checked");
  if (pressed != null) return pressed === "true" ? "on" : "off";
  if (el.getAttribute("aria-selected") === "true") return "on";
  if (/\bis-on\b|\bactive\b|\bMui-selected\b/.test(el.className || "")) return "on";
  return null;
}

/** Roughly where it is, which is how a person would be told where to look. */
function whereOn(el) {
  const box = el.getBoundingClientRect();
  const down = box.top < window.innerHeight * 0.22 ? "top"
    : box.top > window.innerHeight * 0.78 ? "bottom" : "middle";
  const across = box.left < window.innerWidth * 0.3 ? "left"
    : box.left > window.innerWidth * 0.7 ? "right" : "centre";
  return `${down} ${across}`;
}

export function pageActions() {
  /* The whole document, minus the furniture.
     -------------------------------------------------------------------------
     There is no <main> here and no single element that holds the page either -
     the content sits in a different wrapper on different routes - so the page
     is defined by what it is NOT: the rail on the left and the ask pill, which
     are the same everywhere and are not operations on anything. Reading those
     back is how a caller is told that "Reports" is something the applications
     page can do. */
  const root = document.body;
  const chrome = Array.from(document.querySelectorAll(CHROME));
  const inChrome = (el) => chrome.some(c => c.contains(el));

  const found = [];
  const taken = new Set();

  root.querySelectorAll(
    "button, [role='button'], [role='tab'], [role='switch'], [role='menuitem'], a[href]:not([href='#']), summary",
  ).forEach(el => {
    if (inChrome(el) || !visible(el)) return;
    const name = nameOf(el);
    if (!name) return;
    /* the same control drawn twice - a label and the button inside it - is one
       operation, and the one nearest the click is the one that is kept */
    if (found.some(f => f.name === name && f.el.contains(el))) return;
    const twin = found.findIndex(f => f.name === name);
    if (twin >= 0) { if (el.contains(found[twin].el)) return; }
    if (taken.has(el)) return;
    taken.add(el);
    found.push({ el, name, state: stateOf(el), where: whereOn(el) });
  });

  return found;
}

export function pageState(map) {
  const path = window.location.pathname;
  const known = map.pages.find(p => p.path === path)
    || map.pages.find(p => (p.views || []).some(v => v.path === path))
    || null;

  const field = pageSearchField();
  const cards = cardsNow();
  const chosen = cards.find(c => c.selected) || null;

  return {
    url: path + window.location.search,
    page: known ? { id: known.id, label: known.label, what: known.what } : { id: null, label: document.title },
    view: cards.length ? "cards" : (document.querySelector("table") ? "table" : "page"),
    search: field ? field.value : null,
    matches: text(document.querySelector(".forge-wiki-search__hits")) || null,
    count: text(document.querySelector(".forge-table-panel__count")) || null,
    showing: cards.length || document.querySelectorAll("table tbody tr").length || 0,
    selected: chosen ? { id: chosen.id, name: chosen.name, where: chosen.where } : null,
  };
}

/* ── The tools ───────────────────────────────────────────────────────────
   Each one says, in its description, what it is FOR rather than what it does
   to the DOM: that description is the whole of what a model has to go on.
   ========================================================================= */
export function pageTools(say, map) {
  const state = () => pageState(map);

  return [
    {
      name: "where_am_i",
      description:
        "What the user is looking at right now: which page, drawn as a table or as cards, "
        + "what is typed in its search, how many things it is showing, and which one is selected. "
        + "Call this before anything else when a request is about \"this\" or \"these\".",
      inputSchema: { type: "object", properties: {} },
      async execute() {
        return say(JSON.stringify(state(), null, 2));
      },
    },

    {
      name: "read_page",
      description:
        "Read out what is on the page: the first things it is showing, by name. "
        + "Use it to answer \"what is here\", \"how many\", or to find the name of something "
        + "the user described loosely before acting on it.",
      inputSchema: {
        type: "object",
        properties: {
          limit: { type: "number", description: "How many to read out. Default 12, most 40." },
        },
      },
      async execute({ limit } = {}) {
        const n = Math.min(Math.max(Number(limit) || 12, 1), 40);
        const cards = cardsNow();
        const here = state();
        const items = cards.length
          ? cards.slice(0, n).map(c => `${c.name}${c.where ? ` (${c.where})` : ""}${c.selected ? " ← selected" : ""}`)
          : rowsNow(n);
        return say(JSON.stringify({
          ...here,
          items,
          more: Math.max(0, (cards.length || document.querySelectorAll("table tbody tr").length) - items.length),
        }, null, 2));
      },
    },

    {
      name: "search_page",
      description:
        "Type words into the search of the page that is open, the way the user would. "
        + "Use it to narrow what is on screen to one thing - a machine's name, a package, "
        + "an application. Returns how many matched.",
      inputSchema: {
        type: "object",
        properties: { text: { type: "string", description: "What to look for. Empty clears the search." } },
        required: ["text"],
      },
      async execute({ text: what } = {}) {
        const field = pageSearchField();
        if (!field) return say("This page has no search of its own.");
        typeInto(field, String(what ?? ""));
        field.focus();
        await wait();
        const now = state();
        return say(JSON.stringify({ searched: String(what ?? ""), matches: now.matches, showing: now.showing }, null, 2));
      },
    },

    {
      name: "set_view",
      description:
        "Draw the page as cards or as a table. Cards are a stage you can see the whole set on at once; "
        + "the table is the one with every column in it. Only the estate and the applications have both.",
      inputSchema: {
        type: "object",
        properties: { view: { type: "string", enum: ["cards", "table"], description: "Which one." } },
        required: ["view"],
      },
      async execute({ view } = {}) {
        const toggle = document.querySelector(".discovery-cards-toggle, .application-cards-toggle");
        if (!toggle) return say("This page is only drawn one way.");
        const onCards = !!document.querySelector(".forge-wiki-card");
        if ((view === "cards") === onCards) return say(`Already drawn as ${view}.`);
        toggle.click();
        await wait(1200);
        return say(JSON.stringify({ view, ...state() }, null, 2));
      },
    },

    {
      name: "open_card",
      description:
        "Open one of the things on a card page - a machine, an application, a codebase - by its id "
        + "or by its name. It comes to the middle of the stage at reading size, and whatever is "
        + "beside the stage shows what it is. Use read_page first if you do not know the name.",
      inputSchema: {
        type: "object",
        properties: {
          id: { type: "string", description: "The card's id, as read_page gives it." },
          name: { type: "string", description: "Or its name, which may be part of it." },
        },
      },
      async execute({ id, name } = {}) {
        const cards = cardsNow();
        if (!cards.length) return say("Nothing is drawn as cards here - try set_view first.");

        const want = String(name || "").trim().toLowerCase();
        const found = (id && cards.find(c => c.id === id))
          || (want && cards.find(c => c.name.toLowerCase() === want))
          || (want && cards.find(c => c.name.toLowerCase().includes(want)))
          || null;
        if (!found) {
          return say(JSON.stringify({
            opened: null,
            why: "nothing on this page goes by that",
            showing: cards.slice(0, 12).map(c => c.name),
          }, null, 2));
        }

        found.el.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true, view: window }));
        await wait(900);
        return say(JSON.stringify({ opened: { id: found.id, name: found.name, where: found.where } }, null, 2));
      },
    },

    {
      name: "list_page_actions",
      description:
        "Everything the page in front of the user can be told to do right now, found by looking at it: "
        + "the buttons, tabs, toggles and switches it is actually showing, with whether each is on. "
        + "This is how to find out what a page offers - nothing here is a fixed list, so it is right "
        + "even for a page these tools have never seen.",
      inputSchema: {
        type: "object",
        properties: {
          about: { type: "string", description: "Only the ones whose name contains this." },
        },
      },
      async execute({ about } = {}) {
        const want = String(about || "").trim().toLowerCase();
        const acts = pageActions()
          .filter(a => !want || a.name.toLowerCase().includes(want));

        return say(JSON.stringify({
          page: state().page,
          actions: acts.map(a => ({
            name: a.name,
            ...(a.state ? { state: a.state } : {}),
            where: a.where,
          })),
          note: acts.length
            ? "Call do_page_action with one of these names."
            : "This page has no controls of its own showing.",
        }, null, 2));
      },
    },

    {
      name: "do_page_action",
      description:
        "Press one of the controls list_page_actions found, by its name. "
        + "Says what the page looked like afterwards, so the result can be checked.",
      inputSchema: {
        type: "object",
        properties: {
          action: { type: "string", description: "The name, as list_page_actions gave it." },
        },
        required: ["action"],
      },
      async execute({ action } = {}) {
        const want = String(action || "").trim().toLowerCase();
        const acts = pageActions();
        const hit = acts.find(a => a.name.toLowerCase() === want)
          || acts.find(a => a.name.toLowerCase().includes(want))
          || acts.find(a => want.includes(a.name.toLowerCase()));

        if (!hit) {
          return say(JSON.stringify({
            pressed: null,
            why: `nothing on this page is called "${action}"`,
            there_is: acts.map(a => a.name).slice(0, 24),
          }, null, 2));
        }

        hit.el.scrollIntoView?.({ block: "nearest" });
        hit.el.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true, view: window }));
        await wait();

        /* what it did, rather than what it was asked to do - a toggle that was
           already on comes back on, and the caller can see that it did */
        const now = pageActions().find(a => a.name === hit.name);
        return say(JSON.stringify({
          pressed: hit.name,
          ...(now?.state ? { now: now.state } : {}),
          page: state(),
        }, null, 2));
      },
    },

    {
      name: "go_back",
      description: "Back to whatever the user was looking at before - the browser's own back.",
      inputSchema: { type: "object", properties: {} },
      async execute() {
        window.history.back();
        await wait(900);
        return say(JSON.stringify(state(), null, 2));
      },
    },
  ];
}

export default pageTools;
